import type { BillingPeriod, Contract } from '@traceio-org/platform-contracts';
import { addMonths, startedPeriods } from '../../../billing/periods';
import { formatDate, formatPeriod, productName, versionLabel } from '../../../format';

/**
 * The provider statuses under which a contract's subscription no longer collects (platform-api's
 * COLLECTION_STOPPED_STATUSES): no new work is admitted until collection is restored. Mirrored here only to word
 * the warning; platform-api decides admissions.
 */
export const COLLECTION_STOPPED_STATUSES: readonly string[] = ['canceled', 'unpaid', 'incomplete_expired'];

export function billingStopped(contract: Pick<Contract, 'provider'>): boolean {
  return (
    contract.provider !== null &&
    contract.provider.status !== null &&
    COLLECTION_STOPPED_STATUSES.includes(contract.provider.status)
  );
}

/** A contract named for people: its product and plan version (Trace for Brokers: Northwind Freight, Trace for Brokers v1). */
export function contractName(contract: Pick<Contract, 'productKey' | 'planVersion'>): string {
  return `${productName(contract.productKey)}: ${contract.planVersion.planName} ${versionLabel(contract.planVersion.version)}`;
}

/**
 * The contract the views show when the address names none: the one platform-api would pick, the first in force by
 * product key, else the newest (the list is newest first).
 */
export function defaultContract(contracts: readonly Contract[]): Contract | null {
  const inForce = contracts
    .filter((c) => c.status === 'active' || c.status === 'trialing')
    .sort((a, b) => a.productKey.localeCompare(b.productKey));
  return inForce[0] ?? contracts[0] ?? null;
}

/** The view of the Billing section kept in the address: the contract, the period and the page of usage events. */
export interface BillingView {
  contract?: string | undefined;
  period?: string | undefined;
  events?: string | undefined;
}

export function billingTabSearch(view: BillingView): string {
  const params = new URLSearchParams();
  if (view.contract) params.set('contract', view.contract);
  if (view.period) params.set('period', view.period);
  if (view.events) params.set('events', view.events);
  const search = params.toString();
  return search ? `?${search}` : '';
}

export function periodLabel(period: BillingPeriod): string {
  return `${formatPeriod(period)}${period.kind === 'trial' ? ' (trial)' : ''}`;
}

/**
 * The periods a staff member can pick for the usage and line items: the current one (platform-api's default when
 * none is named, the empty value) and every earlier period that started, newest first.
 */
export function periodOptions(contract: Contract, now: number): { value: string; label: string }[] {
  const current = contract.currentPeriod;
  const options = [
    {
      value: '',
      label: current ? `Current period: ${periodLabel(current)}` : 'Latest period',
    },
  ];
  for (const period of startedPeriods(contract, now)) {
    if (current && period.start === current.start) continue;
    options.push({ value: period.start, label: periodLabel(period) });
  }
  return options;
}

/**
 * Where an adjustment may go: the paid period running now and the one that ended last, which is rated shortly after
 * its end. Earlier periods are rated and platform-api refuses them (409, which the dialog shows if the last one was
 * rated already); a trial charges nothing. Newest first.
 */
export function adjustablePeriods(contract: Contract, now: number): BillingPeriod[] {
  const regular = startedPeriods(contract, now).filter((period) => period.kind === 'regular');
  const running = regular.filter((period) => Date.parse(period.end) > now);
  const lastEnded = regular.find((period) => Date.parse(period.end) <= now);
  return lastEnded ? [...running, lastEnded] : running;
}

/** The next period boundaries of a contract after now: the only ends platform-api accepts, as date options. */
export function endOptions(contract: Contract, now: number, count = 12): { value: string; label: string }[] {
  const options: { value: string; label: string }[] = [];
  if (contract.trialEndsAt && Date.parse(contract.trialEndsAt) > now) {
    options.push({
      value: contract.trialEndsAt,
      label: `${formatDate(contract.trialEndsAt)} (end of the trial)`,
    });
  }
  const anchor = new Date(contract.billingAnchor);
  for (let n = 0; options.length < count && n < 12 * 100; n += 1) {
    const end = addMonths(anchor, n + 1);
    if (end.getTime() <= now) continue;
    options.push({ value: end.toISOString(), label: formatDate(end.toISOString()) });
  }
  return options;
}
